/* ------------------------------------------------------------------
  File: Footer.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-07-10
------------------------------------------------------------------ */

// Purpose:
//   - Footer renders the global website footer.
//   - It uses footerConfig as the single source of truth for footer columns,
//     related sites, social links, legal links, and copyright.
//   - Supports internal React Router links and external links.

import { Link } from "react-router-dom";
import {
  LuChevronRight,
  LuExternalLink,
  LuLinkedin,
  LuFacebook,
  LuInstagram,
  LuYoutube,
} from "react-icons/lu";
import { RiTwitterXFill, RiTiktokFill } from "react-icons/ri";

import { footerConfig } from "../../../../app/config/footerConfig";
import "./Footer.css";

const socialIconMap = {
  linkedin: LuLinkedin,
  facebook: LuFacebook,
  instagram: LuInstagram,
  x: RiTwitterXFill,
  youtube: LuYoutube,
  tiktok: RiTiktokFill,
};

const FooterLink = ({ link, variant = "default" }) => {
  const isExternal = link.external || link.href;
  const label = (
    <>
      <p className="u-type-small">{link.label}</p>

      {variant === "external" ? (
        <LuExternalLink className="site-footer__external-icon" aria-hidden="true" />
      ) : (
        <LuChevronRight className="site-footer__link-icon" aria-hidden="true" />
      )}
    </>
  );

  if (isExternal) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noreferrer"
        className="site-footer__link"
      >
        {label}
      </a>
    );
  }

  return (
    <Link to={link.to} className="site-footer__link">
      {label}
    </Link>
  );
};

const Footer = () => {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner l-container l-container--wide">
        <div className="site-footer__main l-grid l-grid--12">
          <nav className="site-footer__nav l-span--9 l-grid l-grid--4" aria-label="Footer navigation">
            {footerConfig.columns.map((column) => (
              <section key={column.title} className="site-footer__column l-stack l-stack--sm">
                <h6 className="site-footer__heading ">{column.title}</h6>

                <ul className="site-footer__list l-stack l-stack--xs">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink link={link} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            <section className="site-footer__column site-footer__column--related l-stack l-stack--md">
              <h6 className="site-footer__heading">
                {footerConfig.relatedSites.title}
              </h6>

              <ul className="site-footer__list l-stack l-stack--xs">
                {footerConfig.relatedSites.links.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} variant="external" />
                  </li>
                ))}
              </ul>
            </section>
          </nav>

          <section className="site-footer__brand l-span--3" aria-label="Accend Holdings">
            <Link to="/" className="site-footer__logo-link" aria-label="Accend Holdings home">
              <img
                src={footerConfig.brand.logo}
                alt={footerConfig.brand.name}
                className="site-footer__logo"
              />
            </Link>

            <ul className="site-footer__social-list" aria-label="Social links">
              {footerConfig.socialLinks.map((social) => {
                const Icon = socialIconMap[social.icon];

                return (
                  <li key={social.label}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={social.label}
                      className="site-footer__social-link"
                    >
                      {Icon && <Icon aria-hidden="true" />}
                    </a>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>

        <div className="site-footer__bottom">
          <p className="site-footer__copyright">
            © {footerConfig.copyright.startYear} {footerConfig.copyright.company}.{" "}
            {footerConfig.copyright.text}
          </p>

          {/* <p className="site-footer__credit">{footerConfig.credit}</p> */}

          <nav className="site-footer__legal" aria-label="Legal links">
            <ul>
              {footerConfig.legalLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.to}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;